import { serialize } from "object-to-formdata";
import request from "../../utils/request";
import { Result } from "../../models/general";

export enum DependentRelationship {
  Spouse = "Spouse",
  Child = "Child",
  Parent = "Parent",
  Sibling = "Sibling",
  Other = "Other",
}

export enum InsuranceStatus {
  Pending = "Pending",
  Active = "Active",
  Inactive = "Inactive",
}

export interface DependentView {
  id: number;
  employeeId: string;
  employeeName?: string;
  department?: string;
  fullName: string;
  dateOfBirth: string;
  age: number;
  relationship: DependentRelationship;
  gender?: string;
  nationality?: string;
  nationalId?: string;
  notes?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  medicalConditions: string[];
  insuranceStatus: InsuranceStatus;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiryDate?: string;
  createdOn: string;
}

export interface DependentListView {
  items: DependentView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DependentStatsView {
  total: number;
  activeInsurance: number;
  pendingInsurance: number;
  children: number;
  spouses: number;
  parents: number;
}

export interface CreateDependentInput {
  // Optional owner — HR/Admin creating a dependent from another employee's profile
  // passes that employee's id; omitted for self-service creates.
  employeeId?: string;
  fullName: string;
  dateOfBirth: string;
  relationship: DependentRelationship;
  gender?: string;
  nationality?: string;
  nationalId?: string;
  notes?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelationship?: string;
  medicalConditions?: string[];
}

export interface UpdateDependentInput extends CreateDependentInput {
  id: number;
}

export interface DependentListQuery {
  search?: string;
  relationship?: DependentRelationship;
  insuranceStatus?: InsuranceStatus;
  department?: string;
  minAge?: number;
  maxAge?: number;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// Employee self-service endpoints
export async function getMyDependents(): Promise<Result<DependentView[]>> {
  const response = await request.get<Result<DependentView[]>>("Dependent/GetMyDependents");
  return response.data;
}

export async function getDependent(id: number): Promise<Result<DependentView>> {
  const response = await request.get<Result<DependentView>>(`Dependent/GetDependent/${id}`);
  return response.data;
}

export async function createDependent(input: CreateDependentInput): Promise<Result<DependentView>> {
  const response = await request.post<Result<DependentView>>("Dependent/CreateDependent", input);
  return response.data;
}

export async function updateDependent(input: UpdateDependentInput): Promise<Result<DependentView>> {
  const response = await request.put<Result<DependentView>>("Dependent/UpdateDependent", input);
  return response.data;
}

export async function archiveDependent(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Dependent/ArchiveDependent/${id}`);
  return response.data;
}

export async function getEmployeeDependents(employeeId: string): Promise<Result<DependentView[]>> {
  const response = await request.get<Result<DependentView[]>>(`Dependent/GetEmployeeDependents/${employeeId}`);
  return response.data;
}

// HR directory endpoints
export async function getDependents(query: DependentListQuery): Promise<Result<DependentListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.relationship !== undefined) params.append("relationship", String(query.relationship));
  if (query.insuranceStatus !== undefined) params.append("insuranceStatus", String(query.insuranceStatus));
  if (query.department) params.append("department", query.department);
  if (query.minAge !== undefined) params.append("minAge", String(query.minAge));
  if (query.maxAge !== undefined) params.append("maxAge", String(query.maxAge));
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDescending !== undefined) params.append("sortDescending", String(query.sortDescending));
  if (query.page !== undefined) params.append("page", String(query.page));
  if (query.pageSize !== undefined) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<DependentListView>>(`Dependent/GetDependents?${params.toString()}`);
  return response.data;
}

export async function getDependentStats(): Promise<Result<DependentStatsView>> {
  const response = await request.get<Result<DependentStatsView>>("Dependent/GetDependentStats");
  return response.data;
}

// Insurance enrollment types
export interface UpdateInsuranceInput {
  id: number;
  insuranceStatus: InsuranceStatus;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiryDate?: string;
}

export interface BulkUpdateInsuranceInput {
  dependentIds: number[];
  insuranceStatus: InsuranceStatus;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiryDate?: string;
}

export interface BulkUpdateInsuranceResultView {
  updatedCount: number;
  totalRequested: number;
}

// Insurance enrollment endpoints (HR only)
export async function updateInsurance(input: UpdateInsuranceInput): Promise<Result<DependentView>> {
  const response = await request.put<Result<DependentView>>("Dependent/UpdateInsurance", input);
  return response.data;
}

export async function bulkUpdateInsurance(input: BulkUpdateInsuranceInput): Promise<Result<BulkUpdateInsuranceResultView>> {
  const response = await request.put<Result<BulkUpdateInsuranceResultView>>("Dependent/BulkUpdateInsurance", input);
  return response.data;
}

// Dependent document types
export enum DependentDocumentType {
  BirthCertificate = "BirthCertificate",
  Passport = "Passport",
  InsuranceCard = "InsuranceCard",
  MedicalCertificate = "MedicalCertificate",
  Other = "Other",
}

export interface DependentDocumentView {
  id: number;
  dependentId: number;
  fileName: string;
  fileExtension: string;
  fileSize: number;
  documentType: DependentDocumentType;
  description?: string;
  createdOn: string;
}

export interface UploadDependentDocumentInput {
  dependentId: number;
  documentType: DependentDocumentType;
  description?: string;
  file: File;
}

// Dependent document endpoints
export async function getDependentDocuments(dependentId: number): Promise<Result<DependentDocumentView[]>> {
  const response = await request.get<Result<DependentDocumentView[]>>(`Dependent/GetDependentDocuments/${dependentId}`);
  return response.data;
}

export async function uploadDependentDocument(input: UploadDependentDocumentInput): Promise<Result<DependentDocumentView>> {
  const response = await request.post<Result<DependentDocumentView>>(
    "Dependent/UploadDependentDocument",
    serialize(input)
  );
  return response.data;
}

export async function deleteDependentDocument(documentId: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`Dependent/DeleteDependentDocument/${documentId}`);
  return response.data;
}

export async function getDependentDocumentUrl(documentId: number): Promise<Result<string>> {
  const response = await request.get<Result<string>>(`Dependent/GetDependentDocumentUrl/${documentId}`);
  return response.data;
}
